import { FlowRangeAggregation } from 'src/app/services/flow-dashboard/flow-dashboard.models';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';

/** Independent axes selected in the Resource Flow sidebar. */
export const FLOW_VALUE_MODES = [
    'total',
    'average',
    'percentile',
    'flow records',
    'players',
] as const;

export type FlowValueMode = (typeof FLOW_VALUE_MODES)[number];

export const FLOW_VALUE_MODE_OPTIONS = [
    { value: 'total', label: 'TOTAL' },
    { value: 'average', label: 'AVERAGE' },
    { value: 'percentile', label: 'MEDIAN / PERCENTILE' },
    { value: 'flow records', label: 'FLOW RECORDS' },
    { value: 'players', label: 'PLAYERS' },
] as const;

/** `deu` (daily engaged users) are the players with a positive flow on the given side. */
export const FLOW_POPULATIONS = ['deu', 'dau'] as const;

export type FlowPopulation = (typeof FLOW_POPULATIONS)[number];

/** Daily HOPA/META restriction for the whole analysis, separate from the DEU/DAU denominator. */
export const FLOW_USER_TYPES = ['all', 'hopa', 'meta'] as const;

export type FlowUserType = (typeof FLOW_USER_TYPES)[number];

/** Table-specific daily flag path. */
const USER_TYPE_COLUMN = {
    hopa: { resources: 'ds.player_day.hopa_user', activity: 'ds.hopa_user' },
    meta: { resources: 'ds.player_day.meta_user', activity: 'ds.meta_user' },
} as const;

/** Adds a User Type condition to a complete WHERE. NULL flags stay excluded. */
export function withFlowUserType(
    where: string,
    userType: FlowUserType,
    table: 'resources' | 'activity'
): string {
    if (userType === 'all') {
        return where;
    }
    const column = USER_TYPE_COLUMN[userType][table];
    return where ? `${where}\n  AND ${column}` : `WHERE ${column}`;
}

/** These percentiles have an equivalent in the existing Resource Flow SQL definitions. */
export const FLOW_PERCENTILES = [50, 75, 90, 99] as const;

export type FlowPercentile = (typeof FLOW_PERCENTILES)[number];

export const FLOW_PERCENTILE_SETTING_IDS: Record<
    FlowPercentile,
    QueryContextSettingID
> = {
    50: QueryContextSettingID.SHOW_PERCENTILE_50,
    75: QueryContextSettingID.SHOW_PERCENTILE_75,
    90: QueryContextSettingID.SHOW_PERCENTILE_90,
    99: QueryContextSettingID.SHOW_PERCENTILE_99,
};

export interface ResourceFlowMetricSettings {
    valueMode: FlowValueMode;
    population: FlowPopulation;
    /** Ascending; only used when `valueMode` is `percentile`. */
    percentiles: FlowPercentile[];
}

export interface ResourceFlowMetricDefinition {
    rangeAggregation: FlowRangeAggregation;
    unit: 'currency' | 'flow records' | 'players';
}

export function getResourceFlowMetricSettings(
    context: IQueryContext
): ResourceFlowMetricSettings {
    const percentiles = FLOW_PERCENTILES.filter(
        (percentile) =>
            context.settings[FLOW_PERCENTILE_SETTING_IDS[percentile]]
    );
    return {
        valueMode:
            context.settings[QueryContextSettingID.FLOW_VALUE_MODE] ?? 'total',
        population:
            context.settings[QueryContextSettingID.FLOW_POPULATION] ?? 'deu',
        percentiles,
    };
}

/** Range aggregation and display unit for the selected metric. */
export function getResourceFlowMetric(
    context: IQueryContext
): ResourceFlowMetricDefinition {
    switch (getResourceFlowMetricSettings(context).valueMode) {
        case 'total':
            return { rangeAggregation: 'sum', unit: 'currency' };
        case 'flow records':
            return { rangeAggregation: 'sum', unit: 'flow records' };
        case 'players':
            return { rangeAggregation: 'sum', unit: 'players' };
        case 'average':
        case 'percentile':
            return { rangeAggregation: 'mean', unit: 'currency' };
    }
}
